import React, { useCallback, useEffect, useState } from 'react'
import {Card, Button, Tag, Typography, Row, Col, Space, Tooltip, message, Pagination} from 'antd';
import {CopyOutlined, EditOutlined} from '@ant-design/icons';
import { apiGetListPartner } from "../../api";
import { useLoading } from '../../context/LoadingContext';
import { mapCompanyAccounts } from '../../assets/constants/constants';
import {Link} from "react-router-dom";
import {
  ACCOUNT_MANAGEMENT,
  PARTNERS_LIST,
  CREATE_PARTNER,
  EDIT_PARTNER,
} from "../../assets/constants/route_path";

const { Title, Text } = Typography;

interface Customer {
  id: string;
  cpCode: string;
  name: string;
  discount: number;
  discountUpgrade: number;
  emailsReport: string[];
  ccEmailsReport: string[];
  bccEmailsReport: string[];
  status: 'Active' | 'Inactive';
}

export default function PartnerList() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { setIsLoading } = useLoading();

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const resCustomers = await apiGetListPartner();
      if (resCustomers.data.partners != null) {
        const filteredCustomers = resCustomers.data.partners
          .filter((item: any) => !mapCompanyAccounts[item.cpCode])
        setCustomers(filteredCustomers);
      } else {
        setCustomers([]);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching customers:', error);
    }
  }, [setIsLoading]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Đã sao chép vào clipboard');
  };

  const renderCustomerCard = (customer: Customer, index: number) => {
    return (
      <Card
        key={customer.cpCode}
        style={{
          marginBottom: 12,
          border: '1px solid #f0f0f0',
          borderRadius: 8
        }}
      >
        <Row justify="space-between" align="top">
          <Col span={24}>
            <div style={{ marginBottom: 12 }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: '#1890ff',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: 16,
                    }}>
                      {index + 1}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Title level={4} style={{margin: 0, marginBottom: 4}}>
                          {customer.name}
                        </Title>
                        <Tag color={customer.status === 'Active' ? 'green' : 'red'}>
                          {customer.status === 'Active' ? 'Hoạt động' : 'Không hoạt động'}
                        </Tag>
                      </div>
                      <Text type="secondary">
                        Mã đối tác: {customer.cpCode} • ID: {customer.id}
                      </Text>
                    </div>
                  </div>
                </Col>
                <Col>
                  <Space>
                    <Link to={ACCOUNT_MANAGEMENT + PARTNERS_LIST + EDIT_PARTNER} state={{ customer }}>
                      <Button
                        style={{color: 'white', background: 'orange'}}
                        icon={<EditOutlined/>}
                      />
                    </Link>
                  </Space>
                </Col>
              </Row>
            </div>

            <Row gutter={32}>
              <Col span={12}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Chiết khấu</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text>{customer.discount}%</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Chiết khấu nâng cấp</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text>{customer.discountUpgrade}%</Text>
                  </div>
                </div>
              </Col>
            </Row>

            <div style={{ marginBottom: 12 }}>
              <Text strong>Email báo cáo</Text>
              <div style={{
                marginTop: 8,
                padding: '8px 12px',
                backgroundColor: '#f5f5f5',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Text style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                  {customer.emailsReport.join(', ') || 'Chưa thiết lập email'}
                </Text>
                <Space>
                  <Tooltip title="Sao chép email">
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(customer.emailsReport.join(', '))}
                      disabled={!customer.emailsReport.length}
                    />
                  </Tooltip>
                </Space>
              </div>
            </div>

            {(customer.ccEmailsReport || customer.bccEmailsReport) && (
              <Row gutter={32}>
                {customer.ccEmailsReport && !!customer.ccEmailsReport.length && (
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Email CC</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {customer.ccEmailsReport.join(', ')}
                        </Text>
                      </div>
                    </div>
                  </Col>
                )}
                {customer.bccEmailsReport && !!customer.bccEmailsReport.length && (
                  <Col span={12}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Email BCC</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {customer.bccEmailsReport.join(', ')}
                        </Text>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            )}
          </Col>
        </Row>
      </Card>
    );
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate pagination
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCustomers = customers.slice(startIndex, endIndex);

  return (
    <div>
      {/* Header with total count */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Đối tác
            </Title>
            <Text type="secondary">
              Quản lý đối tác và cấu hình
            </Text>
          </Col>
          <Col>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div>
                <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                <Text>Tổng số đối tác </Text>
                  {customers.length}
                </Title>
              </div>
              <Link to={ACCOUNT_MANAGEMENT + PARTNERS_LIST + CREATE_PARTNER}>
                <Button type="primary">Thêm mới</Button>
              </Link>
            </div>
          </Col>
        </Row>
      </Card>

      {customers.length > 0 ? (
        <div>
          {paginatedCustomers.map((customer, index) => renderCustomerCard(customer, startIndex + index))}

          {/* Pagination */}
          <Card style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#595959', fontSize: 14 }}>
                Hiển thị <strong>{startIndex + 1}-{Math.min(endIndex, customers.length)}</strong> / <strong>{customers.length}</strong> đối tác
              </div>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={customers.length}
                showSizeChanger
                onChange={(page, size) => {
                  setCurrentPage(page);
                  if (size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                  }
                }}
              />
            </div>
          </Card>
        </div>
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Title level={4} type="secondary">
              Không tìm thấy đối tác
            </Title>
            <Text type="secondary">
              Chưa có dữ liệu đối tác
            </Text>
          </div>
        </Card>
      )}
    </div>
  );
}
